import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { useBreadcrumb } from '@/shared/context/BreadcrumbContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowLeft, Eye } from 'lucide-react'
import { useEncuestaDetail } from '../hooks/useEncuestas'
import { TIPO_CAMPO_LABELS } from '../types'

export function EncuestaPreviewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  useDocumentTitle('Vista Previa')
  const { setOverride, clearOverride } = useBreadcrumb()

  const { data: encuesta, isLoading } = useEncuestaDetail(Number(id))

  useEffect(() => {
    if (encuesta?.titulo) {
      setOverride(`/encuestas/${id}`, encuesta.titulo)
    }
    return () => { clearOverride(`/encuestas/${id}`) }
  }, [encuesta?.titulo, id, setOverride, clearOverride])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!encuesta) {
    return (
      <div className="p-12 text-center">
        <p className="text-lg text-muted-foreground">Encuesta no encontrada</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/encuestas')}>Volver</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/encuestas')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Volver
        </Button>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Eye className="h-6 w-6 text-teal-500" />
          Vista Previa
        </h1>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Simulación de encuesta pública */}
        <Card className="border border-gray-300 dark:border-gray-700">
          {encuesta.imagen && (
            <div className="w-full h-48 overflow-hidden rounded-t-lg">
              <img src={encuesta.imagen} alt={encuesta.titulo} className="w-full h-full object-cover" />
            </div>
          )}
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{encuesta.titulo}</CardTitle>
            {encuesta.descripcion && (
              <p className="text-muted-foreground mt-2">{encuesta.descripcion}</p>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Campo principal */}
            {encuesta.campo_principal_activo && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {encuesta.campo_principal_etiqueta}
                  {encuesta.campo_principal_requerido && <span className="text-destructive ml-1">*</span>}
                </label>
                <input
                  type="text"
                  disabled
                  placeholder={encuesta.campo_principal_placeholder}
                  className="w-full px-3 py-2 border rounded-md bg-muted text-muted-foreground"
                />
              </div>
            )}

            {/* Campos */}
            {encuesta.campos
              .sort((a, b) => a.orden - b.orden)
              .map((campo) => (
                <div key={campo.id} className="space-y-2">
                  <label className="text-sm font-medium">
                    {campo.etiqueta}
                    {campo.requerido && <span className="text-destructive ml-1">*</span>}
                  </label>
                  {campo.descripcion && (
                    <p className="text-xs text-muted-foreground">{campo.descripcion}</p>
                  )}
                  <Badge variant="outline" className="text-xs">{TIPO_CAMPO_LABELS[campo.tipo]}</Badge>

                  {/* Render según tipo */}
                  {campo.tipo === 'texto' && (
                    <input disabled placeholder={campo.placeholder} className="w-full px-3 py-2 border rounded-md bg-muted" />
                  )}
                  {campo.tipo === 'email' && (
                    <input disabled type="email" placeholder={campo.placeholder || 'email@ejemplo.com'} className="w-full px-3 py-2 border rounded-md bg-muted" />
                  )}
                  {campo.tipo === 'numero' && (
                    <input disabled type="number" placeholder={campo.placeholder} className="w-full px-3 py-2 border rounded-md bg-muted" />
                  )}
                  {campo.tipo === 'telefono' && (
                    <input disabled type="tel" placeholder={campo.placeholder || '09XX-XXXX'} className="w-full px-3 py-2 border rounded-md bg-muted" />
                  )}
                  {campo.tipo === 'textarea' && (
                    <textarea disabled rows={3} placeholder={campo.placeholder} className="w-full px-3 py-2 border rounded-md bg-muted" />
                  )}
                  {campo.tipo === 'fecha' && (
                    <input disabled type="date" className="w-full px-3 py-2 border rounded-md bg-muted" />
                  )}
                  {campo.tipo === 'select' && (
                    <select disabled className="w-full px-3 py-2 border rounded-md bg-muted">
                      <option>Seleccionar...</option>
                      {campo.opciones.map(op => <option key={op.id}>{op.valor}</option>)}
                    </select>
                  )}
                  {campo.tipo === 'radio' && (
                    <div className="space-y-1">
                      {campo.opciones.map(op => (
                        <label key={op.id} className="flex items-center gap-2 text-sm">
                          <input type="radio" disabled name={`campo_${campo.id}`} />
                          {op.valor}
                        </label>
                      ))}
                    </div>
                  )}
                  {campo.tipo === 'checkbox' && (
                    <div className="space-y-1">
                      {campo.opciones.map(op => (
                        <label key={op.id} className="flex items-center gap-2 text-sm">
                          <input type="checkbox" disabled />
                          {op.valor}
                        </label>
                      ))}
                    </div>
                  )}
                  {campo.tipo === 'escala' && campo.min_valor != null && campo.max_valor != null && (
                    <div className="flex gap-2 flex-wrap">
                      {Array.from({ length: campo.max_valor - campo.min_valor + 1 }, (_, i) => campo.min_valor! + i).map(val => (
                        <div key={val} className="flex flex-col items-center">
                          <button disabled className="w-10 h-10 border rounded-md bg-muted text-sm font-medium">
                            {val}
                          </button>
                          {campo.escala_etiquetas?.[String(val)] && (
                            <span className="text-[10px] text-muted-foreground mt-1 max-w-[60px] text-center leading-tight">
                              {campo.escala_etiquetas[String(val)]}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

            {encuesta.footer && (
              <div className="pt-4 border-t text-xs text-muted-foreground text-center">
                {encuesta.footer}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
